import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  getNotifications, 
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification
} from '../lib/api';
import { 
  Bell, 
  AlertTriangle, 
  WifiOff, 
  Activity, 
  Check,
  CheckCheck,
  X,
  Zap,
  RefreshCw,
  Trash2,
  Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { cn, formatDate } from '../lib/utils';
import { toast } from 'sonner';

const severityColors = {
  critical: 'bg-red-500/10 text-red-500 border-red-500/20',
  warning: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
  info: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
};

const severityBadgeColors = {
  critical: 'bg-red-500 hover:bg-red-600',
  warning: 'bg-yellow-500 hover:bg-yellow-600',
  info: 'bg-blue-500 hover:bg-blue-600',
};

const alertTypeIcons = {
  charger_offline: WifiOff,
  charger_faulted: AlertTriangle,
  fault_cleared: Check,
  charger_flapping: RefreshCw,
  session_failed: X,
};

const Notifications = () => {
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, critical, warning
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const fetchNotifications = async (reset = false) => {
    try {
      setLoading(true);
      const currentPage = reset ? 1 : page;
      const params = { 
        limit: 50,
        skip: (currentPage - 1) * 50
      };
      
      if (filter === 'unread') params.unread_only = true;
      if (filter === 'critical') params.severity = 'critical';
      if (filter === 'warning') params.severity = 'warning';
      
      const response = await getNotifications(params);
      const newNotifications = response.data || [];
      
      if (reset) {
        setNotifications(newNotifications);
        setPage(1);
      } else {
        setNotifications(prev => [...prev, ...newNotifications]);
      }
      
      setHasMore(newNotifications.length === 50);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      toast.error('Gabim në ngarkimin e njoftimeve');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications(true);
  }, [filter]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markNotificationRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read_by_me: true } : n)
      );
    } catch (error) {
      toast.error('Gabim në shënimin e njoftimit');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read_by_me: true })));
      toast.success('Të gjitha njoftimet u shënuan si të lexuara');
    } catch (error) {
      toast.error('Gabim në shënimin e njoftimeve');
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await deleteNotification(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success('Njoftimi u fshi');
    } catch (error) {
      toast.error('Gabim në fshirjen e njoftimit');
    }
  };

  const getIcon = (alertType, severity) => {
    const Icon = alertTypeIcons[alertType] || Bell;
    return Icon;
  };

  const unreadCount = notifications.filter(n => !n.is_read_by_me).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bell className="h-6 w-6" />
            Njoftimet
          </h1>
          <p className="text-muted-foreground mt-1">
            {unreadCount > 0 ? `${unreadCount} njoftime të palexuara` : 'Të gjitha njoftimet janë lexuar'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[180px]">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Filtro" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Të gjitha</SelectItem>
              <SelectItem value="unread">Të palexuara</SelectItem>
              <SelectItem value="critical">Kritike</SelectItem>
              <SelectItem value="warning">Paralajmërime</SelectItem>
            </SelectContent>
          </Select>
          {unreadCount > 0 && (
            <Button variant="outline" onClick={handleMarkAllAsRead}>
              <CheckCheck className="h-4 w-4 mr-2" />
              Shëno të gjitha
            </Button>
          )}
        </div>
      </div>

      {/* Notifications List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Lista e Njoftimeve</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading && notifications.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <Bell className="h-12 w-12 mb-4 opacity-50" />
              <p className="text-lg">Nuk ka njoftime</p>
              <p className="text-sm">Njoftimet e reja do të shfaqen këtu</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {notifications.map((notification) => {
                const Icon = getIcon(notification.alert_type, notification.severity);
                const isUnread = !notification.is_read_by_me;
                
                return (
                  <div
                    key={notification.id}
                    className={cn(
                      "p-4 hover:bg-muted/50 transition-colors",
                      isUnread && "bg-primary/5"
                    )}
                  >
                    <div className="flex gap-4">
                      <div className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border",
                        severityColors[notification.severity] || severityColors.info
                      )}>
                        <Icon className="h-5 w-5" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className={cn(
                            "font-medium",
                            isUnread && "text-foreground"
                          )}>
                            {notification.title}
                          </p>
                          <Badge className={cn("text-xs", severityBadgeColors[notification.severity])}>
                            {notification.severity === 'critical' ? 'Kritike' : 
                             notification.severity === 'warning' ? 'Paralajmërim' : 'Info'}
                          </Badge>
                          {isUnread && (
                            <span className="w-2 h-2 rounded-full bg-primary" />
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {notification.message}
                        </p>
                        <div className="flex items-center gap-3 mt-2 flex-wrap">
                          {notification.charger_name && (
                            <Badge variant="outline" className="text-xs">
                              {notification.charger_name}
                            </Badge>
                          )}
                          <span className="text-xs text-muted-foreground">
                            {formatDate(notification.created_at)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-1">
                        {isUnread && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleMarkAsRead(notification.id)}
                            title="Shëno si të lexuar"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => handleDelete(notification.id)}
                          title="Fshi"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Load More */}
          {hasMore && notifications.length > 0 && (
            <div className="p-4 border-t">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  setPage(prev => prev + 1);
                  fetchNotifications();
                }}
                disabled={loading}
              >
                {loading ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : null}
                Ngarko më shumë
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Notifications;
